import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from '../Modal';
import TeamPicker from '../TeamPicker';
import useConfig from '../../hooks/useConfig';
import GLOBALS from '../../config/globals';

const FORCED_OUTCOME_KINDS = [
    { value: 'lineupOrder', label: 'Lineup Order #' },
    { value: 'position', label: 'Position' },
    { value: 'specific', label: 'Card ID' },
];

const TEAM_PARAM_KEYS = new Set(['teamNameIDs', 'allowedTeams']);

const renderForcedOutcomeInput = (paramKey, paramDef, value, onChange) => {
    const kind = value?.kind || 'lineupOrder';
    const inner = value?.value ?? '';

    const renderValueInput = () => {
        if (kind === 'lineupOrder') {
            return (
                <select value={inner} onChange={e => onChange(paramKey, { kind, value: e.target.value })}>
                    <option value="">Select...</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                        <option key={n} value={n}>#{n}</option>
                    ))}
                </select>
            );
        }
        if (kind === 'position') {
            return (
                <select value={inner} onChange={e => onChange(paramKey, { kind, value: e.target.value })}>
                    <option value="">Select...</option>
                    {GLOBALS.BATTING_POSITIONS.map(p => (
                        <option key={p} value={p}>{p}</option>
                    ))}
                </select>
            );
        }
        return (
            <input type="text" placeholder="Card ID" value={inner}
                onChange={e => onChange(paramKey, { kind, value: e.target.value })} />
        );
    };

    return (
        <div className="add-constraint-modal__target-group" key={paramKey}>
            <label>{paramDef.description}</label>
            <select
                value={kind}
                onChange={e => onChange(paramKey, { kind: e.target.value, value: '' })}
            >
                {FORCED_OUTCOME_KINDS.map(k => (
                    <option key={k.value} value={k.value}>{k.label}</option>
                ))}
            </select>
            {renderValueInput()}
        </div>
    );
};

const DROPDOWN_OPTIONS = {
    side: GLOBALS.CONSTRAINT_SIDES,
    outcome: GLOBALS.GAME_EVENT_RESULTS,
    rarity: GLOBALS.RARITY_TIERS.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) })),
};

const renderDefaultInput = (paramKey, paramDef, value, onChange) => {
    const options = DROPDOWN_OPTIONS[paramKey];
    if (options) {
        return (
            <div className="add-constraint-modal__field" key={paramKey}>
                <label>{paramDef.description}</label>
                <select value={value ?? ''} onChange={e => onChange(paramKey, e.target.value)}>
                    <option value="">Select...</option>
                    {options.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            </div>
        );
    }
    return (
        <div className="add-constraint-modal__field" key={paramKey}>
            <label>{paramDef.description}</label>
            <input
                type={paramDef.type === 'number' ? 'number' : 'text'}
                placeholder={paramDef.type === 'string[]' ? 'Comma-separated values' : ''}
                value={value ?? ''}
                onChange={e => onChange(paramKey, e.target.value)}
            />
        </div>
    );
};

const buildConstraint = (typeDef, params) => {
    const constraint = { type: typeDef.type };
    for (const [key, paramDef] of Object.entries(typeDef.paramSchema)) {
        const raw = params[key] ?? '';
        if (paramDef.type === 'string[]') {
            if (Array.isArray(raw)) {
                constraint[key] = raw;
            } else {
                constraint[key] = typeof raw === 'string'
                    ? raw.split(',').map(s => s.trim()).filter(Boolean)
                    : raw;
            }
        } else if (paramDef.type === 'number') {
            constraint[key] = Number(raw);
        } else if (paramDef.type === 'ForcedOutcomeTarget') {
            const kind = raw?.kind || 'lineupOrder';
            const val = raw?.value ?? '';
            if (kind === 'lineupOrder') {
                constraint[key] = { kind, order: Number(val) };
            } else if (kind === 'position') {
                constraint[key] = { kind, position: val };
            } else {
                constraint[key] = { kind, cardID: val };
            }
        } else {
            constraint[key] = raw;
        }
    }
    return constraint;
};

const AddConstraintModal = ({ constraintTypeDefs, onAdd, onClose }) => {
    const config = useConfig();
    const [selectedType, setSelectedType] = useState('');
    const [params, setParams] = useState({});

    const typeMap = useMemo(
        () => new Map(constraintTypeDefs.map(t => [t.type, t])),
        [constraintTypeDefs],
    );

    const allTeamOptions = useMemo(() => {
        const groups = config?.teamGroups || [];
        const seen = new Set();
        const teams = [];
        for (const group of groups) {
            for (const t of group.teams) {
                if (!seen.has(t)) {
                    seen.add(t);
                    teams.push([t, t]);
                }
            }
        }
        return teams.sort(([a], [b]) => a.localeCompare(b));
    }, [config]);

    const selectedDef = typeMap.get(selectedType);

    const handleParamChange = (key, value) => {
        setParams(prev => ({ ...prev, [key]: value }));
    };

    const handleAdd = () => {
        if (!selectedDef) return;
        const missing = Object.entries(selectedDef.paramSchema)
            .filter(([, def]) => def.required)
            .some(([key]) => {
                const val = params[key];
                if (val == null || val === '') return true;
                if (Array.isArray(val) && val.length === 0) return true;
                if (typeof val === 'object' && !Array.isArray(val) && val.value === '') return true;
                return false;
            });
        if (missing) return;

        onAdd(buildConstraint(selectedDef, params));
        onClose();
    };

    const renderParamInput = (paramKey, paramDef) => {
        const value = params[paramKey];

        if (paramDef.type === 'string[]' && TEAM_PARAM_KEYS.has(paramKey)) {
            return (
                <div className="add-constraint-modal__field" key={paramKey}>
                    <label>{paramDef.description}</label>
                    <TeamPicker
                        value={Array.isArray(value) ? value : []}
                        onChange={(next) => handleParamChange(paramKey, next)}
                        teamGroups={config?.teamGroups || []}
                        options={allTeamOptions}
                    />
                </div>
            );
        }

        if (paramDef.type === 'ForcedOutcomeTarget') {
            return renderForcedOutcomeInput(paramKey, paramDef, value, handleParamChange);
        }

        return renderDefaultInput(paramKey, paramDef, value, handleParamChange);
    };

    return (
        <Modal onClose={onClose} title="Add Constraint" className="add-constraint-modal">
            <h3>Add Constraint</h3>
            <div className="add-constraint-modal__form">
                <div className="add-constraint-modal__field">
                    <label>Constraint Type</label>
                    <select
                        value={selectedType}
                        onChange={e => {
                            setSelectedType(e.target.value);
                            setParams({});
                        }}
                    >
                        <option value="">Select type...</option>
                        {constraintTypeDefs.map(def => (
                            <option key={def.type} value={def.type}>{def.label}</option>
                        ))}
                    </select>
                </div>

                {selectedDef && (
                    <>
                        <p className="add-constraint-modal__desc">{selectedDef.description}</p>
                        {Object.entries(selectedDef.paramSchema).map(([key, paramDef]) =>
                            renderParamInput(key, paramDef)
                        )}
                        <button
                            type="button"
                            className="btn-primary"
                            onClick={handleAdd}
                        >
                            Add Constraint
                        </button>
                    </>
                )}
            </div>
        </Modal>
    );
};

AddConstraintModal.propTypes = {
    constraintTypeDefs: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        paramSchema: PropTypes.object.isRequired,
    })).isRequired,
    onAdd: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default AddConstraintModal;
